/***
 * PROJECT-QRCODE.WIDGET
 * Edit content for the QRCode
 */

import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { Project, ProjectPopulationImageGroup, ProjectState } from "@/redux/project.types"
import { useEffect, useState } from "react"
import { store } from "@/index"
import { projectCheckQrcode, projectEdit, projectEditQrcodeOptions, projectUpdate } from "@/redux/project.actions"
import { Session } from "@/redux/_session.types"
import Checkbox from "@/components/checkbox"
import TextInput from "@/components/text-input"
import { Language } from "@/redux/language.types"
import QRCode from "react-qr-code"
import { createRoot, Root } from "react-dom/client"
import ReactMarkdown from "react-markdown"
import { TemplateState } from "@/redux/template.types"
import ConfirmButtons from "@/components/confirm-buttons"
import Space from "@/components/space"
import ContextMenu from "@/components/context-menu"
import TranslatorWidget from "./translator.widget"
import LanguageSelection from "@/components/language-selection"
import ProjectStepErrorsWidget from "./project-step-errors.widget"
import Dropdown from "@/components/dropdown"
import { FilterState } from "@/redux/filter.types"
import Link from "@/components/link"
import {
  //  fetchQrcodeLogo,
  upload
} from "@/redux/_upload.actions"
import UploadButtonWidget from "./upload-button.widget"
import { toast } from "react-toastify"
import env from "@/env"
import { generatePDFDemo } from "@/redux/_qrcode.actions"
import ListItem from "@/components/list-item"
import Button from "@/components/button"
import PopulationImageGroupRow from "@/components/population-image-group-row"
import ProjectPopulationImagesModal from "@/modals/project-population-images.modal"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  project: ProjectState
  template: TemplateState
}

interface OwnProps {
  onNext?: Function
  onEdit: Function
}

type Props = StateProps & OwnProps

const CONTEXT_AUTO_TRANSLATION: string = "CONTEXT_AUTO_TRANSLATION"

function ProjectQRCodeWidget(props: Props) {
  const { t } = props

  const [currentLanguage, setCurrentLanguage] = useState<string>(props.project.active.languages[0])

  const [currentContext, setCurrentContext] = useState<string | null>(null)
  const [isDemoPdfGenerating, setIsDemoPdfGenerating] = useState(false)
  const [isPopulationModalOpen, setIsPopulationModalOpen] = useState(false)

  //Create QR code to test the template
  useEffect(() => {
    // Get the DOM element where you want to mount the React component
    const rootElement: HTMLElement | null = document.getElementById("div_qrcode")

    if (rootElement) {
      // Create a root for rendering
      const root: Root = createRoot(rootElement)

      // Conditionally render the component
      root.render(
        props.project.active.qrcodeOptions.hideCode ? (
          <div />
        ) : (
          <QRCode size={140} value={props.project.active.testTemplateLink} />
        )
      )
    }
  }, [props.project.active.testTemplateLink, props.project.active.qrcodeOptions.hideCode])

  function edit() {
    store.dispatch(projectCheckQrcode(props.project.active.languages, props.project.active.qrcodeOptions))
    props.onEdit()
  }

  function editQrcodeContent(languageCode: string, value: string) {
    editQrcodeOptions(
      "content",
      Object.assign({}, props.project.active.qrcodeOptions.content, { [languageCode]: value })
    )
  }

  function editQrcodeOptions(key: string, value: any) {
    store.dispatch(projectEditQrcodeOptions(key, value))
  }

  async function savePopulationImageGroups(groups: ProjectPopulationImageGroup[]) {
    const response: any = await store.dispatch(
      projectUpdate(new Project({ ...props.project.active, populationImageGroups: groups }))
    )
    if (response.error) {
      toast(t("error_saving"), { type: "error" })
    } else {
      store.dispatch(projectEdit("populationImageGroups", groups))
    }
  }

  function next(isVisited, isConfirmed) {
    if (props.onNext) {
      props.onNext(isVisited, isConfirmed)
    }
  }

  const [uploadedLogo, setUploadedLogo] = useState<string | null>(null)

  useEffect(() => {
    async function checkLogo() {
      const id = props.project?.active?.id
      if (!id) return

      const logoUrl = `${env.REACT_APP_URL_SPACE}/QrcodeLogos/${id}.png`

      try {
        const res = await fetch(logoUrl, { method: "GET" })

        if (res.ok) {
          setUploadedLogo(logoUrl)
        } else {
          console.warn("Logo not found or forbidden:", res.status)
          setUploadedLogo(null)
        }
      } catch (err) {
        console.error("Fetch error while checking logo:", err)
        setUploadedLogo(null)
      }
    }

    checkLogo()
  }, [props.project?.active?.id])

  async function uploadFile(event: File[]) {
    const file = event[0]
    if (!file) return

    const reader = new FileReader()
    const type = file.type
    const id = props.project?.active?.id
    editQrcodeOptions("projectId", id)
    const folder = "QrcodeLogos"

    reader.onloadend = async function (e) {
      const fileBase64 = (e.target?.result as string) || ""

      setUploadedLogo(fileBase64)

      try {
        await sendFile(id, fileBase64, folder, type)
        toast.success("Fichier uploadé avec succès")
      } catch (error) {
        toast.error("Erreur lors de l’upload")
        console.error(error)
      }
    }

    reader.readAsDataURL(file)
  }

  async function sendFile(id: string, file: string, folder: string, type: string): Promise<any> {
    try {
      const response = await store.dispatch(upload(id, file, folder, type))
      return response
    } catch (error) {
      console.error("Erreur lors de l’upload :", error)
      throw error
    }
  }

  async function generateDemoPdf() {
    setIsDemoPdfGenerating(true)
    const pdf: any = await store.dispatch(generatePDFDemo(props.project.active.id, props.project.active.qrcodeOptions))
    setIsDemoPdfGenerating(false)

    if (pdf.error) {
      toast(t("utils_error"), { type: "error" })
    } else {
      window.open(env.REACT_APP_URL_QRCODE + "/public/" + props.project.active.id + "/" + pdf.file, "_blank")
    }
  }

  return (
    <div style={{ padding: "10px 32px" }}>
      <div className="height-10" />

      <h3>{t("project_invitation_qrcode_options")}</h3>

      <div className="height-10" />

      {props.project.active.languages.length > 1 && (
        <div className="flex">
          <div>
            <p className="grey-t">{t("utils_translations")}</p>

            <LanguageSelection
              languages={props.project.active.languages}
              activeLang={currentLanguage}
              onClick={(language: Language) => setCurrentLanguage(language.code)}
            />
          </div>

          <Space />

          <div className="flex flex-dcol">
            <Space />

            <u className="_hover grey-t" onClick={() => setCurrentContext(CONTEXT_AUTO_TRANSLATION)}>
              {t("auto_translate")}
            </u>

            <div className="rel">
              {currentContext === CONTEXT_AUTO_TRANSLATION && (
                <ContextMenu positionRight onClose={() => setCurrentContext(null)}>
                  <div style={{ padding: "10px 20px" }}>
                    <p>{t("auto_translate")}</p>

                    <TranslatorWidget
                      targetLang={currentLanguage}
                      onTranslate={(texts: string[], language: Language) => {
                        editQrcodeContent(language.code, texts[0])
                        edit()
                        setCurrentContext(null)
                      }}
                      languages={props.project.active.languages}
                      sourceTexts={[props.project.active.qrcodeOptions.content]}
                      onTargetChange={(lang: Language) => setCurrentLanguage(lang.code)}
                    />
                  </div>
                </ContextMenu>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="height-10" />

      <div className="flex">
        <div className="flex2">
          <TextInput
            title={t("project_invitation_qrcode_sender_name")}
            placeholder={props.project.active.qrcodeOptions.senderName}
            value={props.project.active.qrcodeOptions.senderName}
            onBlur={edit}
            onChange={(e) => editQrcodeOptions("senderName", e.value)}
            isFullWidth
          />

          <TextInput
            title={t("project_invitation_qrcode_sender_address")}
            value={props.project.active.qrcodeOptions.senderAddress}
            help={t("project_invitation_qrcode_sender_address_help")}
            onBlur={edit}
            onChange={(e) => editQrcodeOptions("senderAddress", e.value)}
            isFullWidth
          />

          <Checkbox
            onClick={() => {
              editQrcodeOptions("hideAddress", !props.project.active.qrcodeOptions.hideAddress)
              edit()
            }}
            active={props.project.active.qrcodeOptions.hideAddress}
            text={t("project_invitation_qrcode_address_display")}
          />

          <Checkbox
            onClick={() => {
              editQrcodeOptions("hideCode", !props.project.active.qrcodeOptions.hideCode)
              edit()
            }}
            active={props.project.active.qrcodeOptions.hideCode}
            text={t("project_invitation_qrcode_sender_display")}
          />

          <Checkbox
            onClick={() => {
              editQrcodeOptions("displayAttribute", !props.project.active.qrcodeOptions.displayAttribute)
              edit()
            }}
            active={props.project.active.qrcodeOptions.displayAttribute}
            text={t("project_invitation_qrcode_attribute")}
          />

          {props.project.active.qrcodeOptions.displayAttribute && (
            <Dropdown
              displayField="name"
              isNullAllowed
              list={props.filter.list}
              onSelect={(e) => {
                editQrcodeOptions("attributeToDisplay", e.id ? e.name : null)
                edit()
              }}
              title={t("project_invitation_qrcode_attribute_name")}
              value={props.project.active.qrcodeOptions.attributeToDisplay}
            />
          )}

          {
            // Display a second attribute for the QR Code
            props.project.active.qrcodeOptions.displayAttribute && (
              <Checkbox
                onClick={() => {
                  editQrcodeOptions("displayAttribute2", !props.project.active.qrcodeOptions.displayAttribute2)
                  edit()
                }}
                active={props.project.active.qrcodeOptions.displayAttribute2}
                text={t("project_invitation_qrcode_attribute_2")}
              />
            )
          }

          {props.project.active.qrcodeOptions.displayAttribute2 && (
            <Dropdown
              displayField="name"
              isNullAllowed
              list={props.filter.list}
              onSelect={(e) => {
                editQrcodeOptions("attributeToDisplay2", e.id ? e.name : null)
                edit()
              }}
              title={t("project_invitation_qrcode_attribute_name")}
              value={props.project.active.qrcodeOptions.attributeToDisplay2}
            />
          )}

          <div className="height-20" />

          <div className="grey-t">{t("project_invitation_qrcode_content")}</div>

          <textarea
            style={{
              resize: "none",
              height: "300px",
              width: "calc(100% - 20px)",
              border: "none",
              padding: "6px 10px",
              backgroundColor: "#eaeaea",
              borderRadius: "8px",
              margin: "12px 0px"
            }}
            onChange={(e) => editQrcodeContent(currentLanguage, e.target.value)}
            onBlur={edit}
            value={props.project.active.qrcodeOptions.content[currentLanguage]}></textarea>

          {!uploadedLogo ? (
            <UploadButtonWidget
              acceptedFiles="image/png"
              maxSize={1000000}
              onUpload={(file) => uploadFile(file)}
              title={t("project_invitation_qrcode_logo_select_require_title")}
              showFrameStyle={true}
            />
          ) : (
            <div className="flex flex-col gap-2 items-start">
              <div className="text-input-title flex">
                <span className="grey-t">{t("project_invitation_qrcode_logo_select_title")}</span>
              </div>
              <img
                src={uploadedLogo}
                alt="Logo uploaded"
                style={{
                  width: 50,
                  height: "auto",
                  objectFit: "contain",
                  zIndex: 2
                }}
              />
              <UploadButtonWidget
                acceptedFiles="image/png"
                maxSize={1000000}
                onUpload={(file) => uploadFile(file)}
                title={t("project_invitation_qrcode_logo_change_title")}
                showFrameStyle={true}
              />
            </div>
          )}

          <div className="height-10" />

          <Checkbox
            active={props.project.active.qrcodeOptions.usePopulationImages}
            onClick={() =>
              editQrcodeOptions("usePopulationImages", !props.project.active.qrcodeOptions.usePopulationImages)
            }
            text={t("project_image_population_enable")}
          />

          {props.project.active.qrcodeOptions.usePopulationImages && (
            <div>
              <div className="height-10" />

              {props.project.active.populationImageGroups.length > 0 && (
                <div
                  className="list-container"
                  style={{ border: "1px solid #ececec", borderRadius: 8, overflow: "hidden" }}>
                  {props.project.active.populationImageGroups.map((group) => (
                    <ListItem key={group.id}>
                      <PopulationImageGroupRow group={group} />
                    </ListItem>
                  ))}
                </div>
              )}

              <div className="height-20" />

              <Button className="light" isFullWidth onClick={() => setIsPopulationModalOpen(true)}>
                {t("project_image_population_add")}
              </Button>
            </div>
          )}

          {isPopulationModalOpen && (
            <ProjectPopulationImagesModal
              groups={props.project.active.populationImageGroups}
              onClose={() => setIsPopulationModalOpen(false)}
              onSave={(groups) => {
                savePopulationImageGroups(groups)
                setIsPopulationModalOpen(false)
              }}
            />
          )}

          <div className="height-20" />
        </div>

        <div
          className="flex3 rel"
          style={{
            fontFamily: "sans-serif",
            minWidth: "570px"
          }}>
          <img src={require("@/assets/qrcode.png")} width="100%" alt="qrcode" />
          <Link className="text-center mt-2" onClick={() => (isDemoPdfGenerating ? undefined : generateDemoPdf())}>
            {isDemoPdfGenerating ? "Génération du PDF de démonstration..." : "Générer un PDF de démonstration"}
          </Link>

          <div
            className="abs"
            style={{
              backgroundColor: "white",
              width: "200px",
              height: "100px",
              left: "68px",
              fontSize: "10px",
              top: "100px"
            }}>
            <div>
              {uploadedLogo && (
                <img
                  src={uploadedLogo}
                  alt="Logo uploaded"
                  style={{
                    width: 50,
                    height: "auto",
                    objectFit: "contain",
                    zIndex: 2
                  }}
                />
              )}

              <div className="mt-2">{props.project.active.qrcodeOptions.senderName}</div>
            </div>

            {props.project.active.qrcodeOptions.senderAddresses.map((x, i) => (
              <div key={i}>{x}</div>
            ))}
          </div>

          <div
            className="abs"
            style={{
              backgroundColor: "white",
              width: "200px",
              height: "100px",
              left: "300px",
              fontSize: "10px",
              fontWeight: 700,
              top: "180px"
            }}>
            <div>{`${props._session.userName}`}</div>

            {!props.project.active.qrcodeOptions.hideAddress && (
              <div>
                <div>{`1 Boulevard XXX`}</div>
                <div>{`44100 Nantes`}</div>
              </div>
            )}
          </div>

          <div className="abs project-invitation-body">
            <ReactMarkdown>{props.project.active.qrcodeOptions.content[currentLanguage]}</ReactMarkdown>
          </div>

          <img
            className="abs"
            src={require("@/assets/invitation_instructions.png")}
            width="83%"
            alt="invitation_instructions"
            style={{
              top: 486,
              left: 47
            }}
          />

          {props.project.active.qrcodeOptions.hideCode && (
            <div
              className="abs"
              style={{
                width: 200,
                height: 100,
                backgroundColor: "#fff",
                top: 580,
                left: 290
              }}
            />
          )}
        </div>
      </div>

      <ProjectStepErrorsWidget alert={props.project.active.alerts.qrcode} step="qrcode" />

      {props.onNext && (
        <ConfirmButtons
          onCancel={() => next(false, false)}
          onConfirm={() => next(true, true)}
          onConfirmLater={() => next(true, false)}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  project: state.project,
  template: state.template
})

export default connect(mapStateToProps)(withTranslation()(ProjectQRCodeWidget))
